import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, UserCog, RefreshCcw, Sticker, Image as ImageIcon, Trash2, PackageCheck, Lock, CheckCircle2, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";
import { DataTable } from "../components/shared/DataTable";
import { StatusBadge } from "../components/shared/StatusBadge";
import { ErrorState } from "../components/shared/ErrorState";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { ChangeDriverDialog } from "../components/routes/ChangeDriverDialog";
import { MoveDeliveryDialog } from "../components/routes/MoveDeliveryDialog";
import { PodViewerDialog } from "../components/deliveries/PodViewerDialog";
import { SpecialInstructionDialog } from "../components/routes/SpecialInstructionDialog";
import { RouteMap } from "../components/routes/RouteMap";
import { apiClient, getApiErrorMessage } from "../lib/apiClient";
import { postWithVersion, isVersionConflict, conflictMessage } from "../lib/conflict";
import { formatDate, formatDistanceKm, formatDurationHm } from "../lib/utils";
import { deliveryStatusMeta, distanceSourceMeta, packingStatusMeta, packingStateMeta } from "../lib/statusMaps";

function Detail({ label, value, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-xs uppercase tracking-wide text-text-muted">{label}</p>
      <p className="mt-1 text-sm font-medium text-text-main">{value || "—"}</p>
    </div>
  );
}

export default function RouteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get("highlight");
  const stopsRef = useRef(null);
  const [state, setState] = useState({ loading: true, error: null, route: null });
  const [changeDriverOpen, setChangeDriverOpen] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [moving, setMoving] = useState(null);
  const [podDelivery, setPodDelivery] = useState(null);
  const [instructionDelivery, setInstructionDelivery] = useState(null);
  const [variant, setVariant] = useState(1);
  const [useRealDistance, setUseRealDistance] = useState(false);
  const [reoptimizing, setReoptimizing] = useState(false);
  const [outlet, setOutlet] = useState(null);
  const [driver, setDriver] = useState(null);
  const [roadLegs, setRoadLegs] = useState(null);
  const [roadInfo, setRoadInfo] = useState(null);
  const [packingSummary, setPackingSummary] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [marking, setMarking] = useState(false);
  const [marquee, setMarquee] = useState(null);
  const tableWrapRef = useRef(null);
  const draggingRef = useRef(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setState({ loading: true, error: null, route: null });
    try {
      const { data } = await apiClient.get(`/routes/${id}`);
      setState({ loading: false, error: null, route: data });
      if (data.driver_id) {
        apiClient.get(`/drivers/${data.driver_id}`).then(({ data: d }) => setDriver(d)).catch(() => setDriver(null));
      } else {
        setDriver(null);
      }
    } catch (error) {
      if (!silent) setState({ loading: false, error: getApiErrorMessage(error, "Could not load this route."), route: null });
    }
  }, [id]);

  useEffect(() => {
    apiClient.get("/outlet/settings").then(({ data }) => setOutlet(data)).catch(() => setOutlet(null));
  }, []);

  useEffect(() => { load(); setVariant(1); }, [load]);

  // Fetch road-following geometry for the map. Keyed on the ordered-stop
  // signature so it fetches once and only re-fetches when the ordering/coords
  // actually change (e.g. after a re-optimize) — NOT on the 15s live poll,
  // which returns the same signature. Backend also caches per ordering.
  const geoSig = (state.route?.deliveries || [])
    .map((d) => `${d.id}:${d.delivery_order_id}:${d.latitude},${d.longitude}`).join("|");
  useEffect(() => {
    if (!state.route || !geoSig) { setRoadLegs(null); setRoadInfo(null); return; }
    let cancelled = false;
    apiClient.get(`/routes/${id}/geometry`)
      .then(({ data }) => {
        if (cancelled) return;
        setRoadLegs(data?.legs || null);
        setRoadInfo(data?.source === "osrm" && data?.distance_m != null
          ? { distance_m: data.distance_m, duration_s: data.duration_s } : null);
      })
      .catch(() => { if (!cancelled) { setRoadLegs(null); setRoadInfo(null); } });
    return () => { cancelled = true; };
  }, [id, geoSig]); // eslint-disable-line react-hooks/exhaustive-deps

  // Per-route packing summary (tiffin-type counts + pack totals). Available
  // once the route is optimized; refreshes when the stop set/order changes.
  useEffect(() => {
    if (!state.route?.distance) { setPackingSummary(null); return; }
    let cancelled = false;
    apiClient.get(`/routes/${id}/packing-summary`)
      .then(({ data }) => { if (!cancelled) setPackingSummary(data); })
      .catch(() => { if (!cancelled) setPackingSummary(null); });
    return () => { cancelled = true; };
  }, [id, state.route?.distance, geoSig]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll silently while the driver has an in-progress route so the live location marker keeps moving.
  useEffect(() => {
    if (!state.route?.tracking_enabled) return;
    const timer = setInterval(() => load(true), 15000);
    return () => clearInterval(timer);
  }, [state.route?.tracking_enabled, load]);

  const scrolledHighlightRef = useRef(null);
  useEffect(() => {
    if (!highlightId || !stopsRef.current || scrolledHighlightRef.current === highlightId) return;
    const row = stopsRef.current.querySelector(`[data-testid="table-row-${highlightId}"]`);
    if (row) {
      row.scrollIntoView({ behavior: "smooth", block: "center" });
      scrolledHighlightRef.current = highlightId;
    }
  }, [highlightId, state.route]);

  const handleReoptimize = async () => {
    const nextVariant = variant >= 6 ? 1 : variant + 1;
    setReoptimizing(true);
    try {
      const { data } = await postWithVersion(`/routes/${id}/reoptimize`, {
        variant: nextVariant, use_real_distance: useRealDistance,
      }, state.route?.updated_at);
      setVariant(nextVariant);
      setConflict(null);
      setState({ loading: false, error: null, route: data });
      toast.success(`Variant ${nextVariant} of 6 applied — ${formatDistanceKm(data.distance)}, ${formatDurationHm(data.duration)} (incl. 3 min/stop service time)`);
    } catch (error) {
      if (isVersionConflict(error)) {
        setConflict(conflictMessage(error));
        toast.warning(conflictMessage(error));
        load(true);  // reload the latest route so the next attempt uses a fresh version
      } else {
        toast.error(getApiErrorMessage(error, "Could not re-optimize this route."));
      }
    } finally {
      setReoptimizing(false);
    }
  };

  const [recalculating, setRecalculating] = useState(false);
  const handleRecalculateRealDistance = async () => {
    setRecalculating(true);
    try {
      const { data } = await postWithVersion(`/routes/${id}/reoptimize`, {
        variant, use_real_distance: true,
      }, state.route?.updated_at);
      setConflict(null);
      setState({ loading: false, error: null, route: data });
      const meta = distanceSourceMeta(data.distance_source);
      toast.success(`Recalculated — ${formatDistanceKm(data.distance)}, ${formatDurationHm(data.duration)} (${meta.label})`);
    } catch (error) {
      if (isVersionConflict(error)) {
        setConflict(conflictMessage(error));
        toast.warning(conflictMessage(error));
        load(true);
      } else {
        toast.error(getApiErrorMessage(error, "Could not recalculate this route."));
      }
    } finally {
      setRecalculating(false);
    }
  };

  const handleDeleteRoute = async () => {
    setDeleting(true);
    try {
      await apiClient.delete(`/routes/${id}`);
      toast.success(`Route #${id} deleted — its stops are back in the unassigned pool for that date.`);
      navigate("/routes");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not delete this route."));
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  if (state.loading) {
    return (
      <div data-testid="route-detail-page">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="mt-4 h-64 w-full" />
      </div>
    );
  }

  if (state.error || !state.route) {
    return (
      <div data-testid="route-detail-page">
        <PageHeader title="Route" actions={<Button variant="secondary" onClick={() => navigate("/routes")} data-testid="route-detail-back-button"><ArrowLeft size={16} strokeWidth={1.5} /> Back</Button>} />
        <ErrorState message={state.error || "Route not found."} onRetry={load} />
      </div>
    );
  }

  const r = state.route;
  const distMeta = distanceSourceMeta(r.distance_source);
  const canRecalculate = r.distance_source !== "ors_matrix" && r.distance_source !== "osrm_matrix" && r.deliveries?.length > 0;
  const skippedStops = r.deliveries?.filter((d) => d.packing_status === 2) || [];
  const locked = !!r.packing_started;
  const routeStarted = !!r.started_at;
  const routeCompleted = (r.deliveries_total || 0) > 0 && r.deliveries_done >= r.deliveries_total;
  const changeDriverLocked = routeStarted || routeCompleted;
  const allPacked = (r.deliveries?.length || 0) > 0 &&
    r.deliveries.every((d) => d.packing_status === 1 || d.packing_status === 2);

  const stops = r.deliveries || [];
  const pendingIds = stops.filter((d) => d.delivery_status === 1).map((d) => d.id);
  const pendingIdSet = new Set(pendingIds);
  const allSelected = pendingIds.length > 0 && pendingIds.every((pid) => selectedIds.has(pid));

  const toggleOne = (did) => setSelectedIds((s) => {
    const n = new Set(s);
    if (n.has(did)) n.delete(did); else n.add(did);
    return n;
  });
  const toggleAll = () => setSelectedIds(() => (allSelected ? new Set() : new Set(pendingIds)));
  const exitSelect = () => { setSelectMode(false); setSelectedIds(new Set()); setMarquee(null); draggingRef.current = false; };

  const handleMarkDelivered = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setMarking(true);
    try {
      const { data } = await apiClient.post("/deliveries/bulk-status", { ids, status: 2 });
      toast.success(`Marked ${data.updated} stop(s) delivered${data.failed?.length ? ` · ${data.failed.length} failed` : ""}.`);
      exitSelect();
      load(true);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not mark stops delivered."));
    } finally {
      setMarking(false);
    }
  };

  // Marquee drag-select (like selecting files in a folder): drag a rectangle over
  // the stops table to select every pending row it touches.
  const marqueeRectFromEvent = (e) => {
    const wrap = tableWrapRef.current;
    const box = wrap.getBoundingClientRect();
    return { x: e.clientX - box.left + wrap.scrollLeft, y: e.clientY - box.top + wrap.scrollTop };
  };
  const applyMarqueeSelection = (rect) => {
    const wrap = tableWrapRef.current;
    if (!wrap) return;
    const box = wrap.getBoundingClientRect();
    const x1 = Math.min(rect.x0, rect.x1), x2 = Math.max(rect.x0, rect.x1);
    const y1 = Math.min(rect.y0, rect.y1), y2 = Math.max(rect.y0, rect.y1);
    const next = new Set();
    wrap.querySelectorAll('[data-testid^="table-row-"]').forEach((row) => {
      const did = Number(row.getAttribute("data-testid").replace("table-row-", ""));
      if (!pendingIdSet.has(did)) return;
      const rr = row.getBoundingClientRect();
      const ry1 = rr.top - box.top + wrap.scrollTop, ry2 = rr.bottom - box.top + wrap.scrollTop;
      if (ry2 >= y1 && ry1 <= y2 && (x2 >= 0) && (x1 <= box.width)) next.add(did);
    });
    setSelectedIds(next);
  };
  const onWrapMouseDown = (e) => {
    if (!selectMode) return;
    if (e.target.closest('button, a, input, [role="checkbox"]')) return;
    e.preventDefault();
    const p = marqueeRectFromEvent(e);
    draggingRef.current = true;
    setMarquee({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
  };
  const onWrapMouseMove = (e) => {
    if (!draggingRef.current) return;
    const p = marqueeRectFromEvent(e);
    setMarquee((m) => {
      const next = { ...m, x1: p.x, y1: p.y };
      applyMarqueeSelection(next);
      return next;
    });
  };
  const endMarquee = () => { draggingRef.current = false; setMarquee(null); };

  return (
    <div data-testid="route-detail-page">
      <PageHeader
        title={`Route #${r.id}`}
        description={formatDate(r.date)}
        actions={
          <Button variant="secondary" onClick={() => navigate("/routes")} data-testid="route-detail-back-button"><ArrowLeft size={16} strokeWidth={1.5} /> Back</Button>
        }
      />

      {conflict && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="route-detail-conflict-banner">
          <p className="font-medium text-amber-900">{conflict}</p>
          <p className="mt-1 text-xs text-amber-800">
            We've loaded the latest version of this route. Review it, then try your edit again.
          </p>
        </div>
      )}

      {r.deliveries_done > 0 && (
        <p className="mb-3 text-xs text-text-muted" data-testid="route-detail-delete-locked-note">
          This route can't be deleted — it already has delivered stops.
        </p>
      )}

      {skippedStops.length > 0 && (
        <div className="mb-4 rounded-md border border-danger/30 bg-danger-bg p-3" data-testid="route-detail-packing-skipped-banner">
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-danger">
            {skippedStops.length} tiffin{skippedStops.length > 1 ? "s" : ""} skipped from packing — let the driver know
          </p>
          <div className="flex flex-wrap gap-1.5">
            {skippedStops.map((s) => (
              <span key={s.id} className="rounded-full bg-surface px-2.5 py-1 text-xs font-medium text-danger shadow-sm" data-testid={`route-detail-packing-skipped-chip-${s.id}`}>
                #{s.delivery_order_id} {s.customer_name || `Delivery #${s.id}`}
              </span>
            ))}
          </div>
        </div>
      )}

      <Tabs defaultValue="overview" className="w-full">
        <TabsList data-testid="route-detail-tabs">
          <TabsTrigger value="overview" data-testid="route-detail-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="reoptimize" data-testid="route-detail-tab-reoptimize">
            Re-optimize {locked && <Lock size={12} strokeWidth={2} className="ml-1 inline" />}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="mb-6 grid gap-4 lg:grid-cols-2">
            <Card className="flex flex-col" data-testid="route-overview-card">
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle>{r.driver_name ? (
                  <Link to={`/drivers/${r.driver_id}`} className="text-primary hover:underline" data-testid="route-driver-link">{r.driver_name}</Link>
                ) : "Unassigned"}</CardTitle>
                <StatusBadge label={packingStateMeta(r.packing_state).label} variant={packingStateMeta(r.packing_state).variant} data-testid="route-detail-packing-state-badge" />
              </CardHeader>
              <CardContent className="flex flex-col gap-4 lg:flex-1">
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-2">
                  <Detail label="Deliveries" value={`${r.deliveries_done}/${r.deliveries_total}`} testId="route-detail-deliveries" />
                  <div data-testid="route-detail-distance">
                    <p className="text-xs uppercase tracking-wide text-text-muted">Distance</p>
                    <p className="mt-1 text-sm font-medium text-text-main">{formatDistanceKm(r.distance) || "—"}</p>
                    <StatusBadge label={distMeta.label} variant={distMeta.variant} className="mt-1 text-[10px]" data-testid="route-detail-distance-source-badge" />
                    {roadInfo && (
                      <p className="mt-1 text-[11px] text-text-muted" data-testid="route-detail-road-distance">
                        <span className="font-medium text-success">Road: {formatDistanceKm(roadInfo.distance_m)}</span>
                        {roadInfo.duration_s != null ? ` · ${formatDurationHm(roadInfo.duration_s)} drive` : ""}
                      </p>
                    )}
                  </div>
                  <Detail label="Duration" value={formatDurationHm(r.duration)} testId="route-detail-duration" />
                  <Detail label="Type" value={r.status_label} testId="route-detail-status-label" />
                </div>

                <div className="flex flex-col border-t border-border pt-4">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">Actions</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline" className="w-full justify-center" disabled={changeDriverLocked}
                      title={routeCompleted ? "Locked — this route is completed" : (routeStarted ? "Locked — driver has already started this route" : undefined)}
                      onClick={() => setChangeDriverOpen(true)} data-testid="route-detail-change-driver-button"
                    >
                      <UserCog size={16} strokeWidth={1.5} /> Change Driver
                    </Button>
                    <Button variant="accent" className="w-full justify-center" asChild data-testid="route-detail-stickers-button">
                      <Link to={`/routes/${r.id}/stickers`}><Sticker size={16} strokeWidth={1.5} /> Print Stickers</Link>
                    </Button>
                    {allPacked ? (
                      <Button className="w-full justify-center bg-success text-white hover:bg-success/90" asChild data-testid="route-detail-packing-button">
                        <Link to={`/routes/${r.id}/packing`}><CheckCircle2 size={16} strokeWidth={1.75} /> Packed</Link>
                      </Button>
                    ) : (
                      <Button variant="outline" className="w-full justify-center" asChild disabled={!r.distance} title={!r.distance ? "This route hasn't been optimized yet — contact admin to optimize it before packing" : undefined} data-testid="route-detail-packing-button">
                        {r.distance ? <Link to={`/routes/${r.id}/packing`}><PackageCheck size={16} strokeWidth={1.5} /> Start Packing</Link> : <span><PackageCheck size={16} strokeWidth={1.5} /> Start Packing</span>}
                      </Button>
                    )}
                    <Button
                      variant="danger" className="w-full justify-center"
                      disabled={r.deliveries_done !== 0}
                      title={r.deliveries_done !== 0 ? "Can't delete — route already has delivered stops" : undefined}
                      onClick={() => setDeleteOpen(true)} data-testid="route-detail-delete-button"
                    >
                      <Trash2 size={16} strokeWidth={1.5} /> Delete Route
                    </Button>
                  </div>
                </div>

                {packingSummary && (
                  <div className="border-t border-border pt-4" data-testid="route-detail-packing-summary">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
                      Packing Summary · {packingSummary.total_tiffins} tiffin{packingSummary.total_tiffins === 1 ? "" : "s"}
                    </p>
                    <div className="flex flex-wrap gap-2" data-testid="route-detail-packing-plan-counts">
                      {packingSummary.plan_counts.map((pc) => (
                        <span key={pc.label} className="flex items-baseline gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-sm font-semibold text-text-main" data-testid={`route-detail-plan-count-${pc.label.replace(/\s+/g, "-").toLowerCase()}`}>
                          <span className="font-heading text-base font-extrabold text-primary">{pc.count}×</span> {pc.label}
                        </span>
                      ))}
                    </div>
                    <p className="mb-1.5 mt-3 text-xs font-semibold uppercase tracking-wide text-text-muted">Totals to pack</p>
                    <div className="flex flex-wrap gap-2" data-testid="route-detail-packing-totals">
                      {packingSummary.totals.map((t) => (
                        <span key={t.label} className="flex items-baseline gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-sm font-semibold text-text-main" data-testid={`route-detail-packing-total-${t.label.replace(/\s+/g, "-").toLowerCase()}`}>
                          <span className="font-heading text-base font-extrabold text-primary">{t.quantity}×</span> {t.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="h-[320px] overflow-hidden rounded-md border border-border lg:aspect-square lg:h-auto" style={{ isolation: "isolate" }} data-testid="route-detail-map-container">
              <RouteMap
                outlet={outlet} driver={driver} stops={r.deliveries}
                live={{ lat: r.live_lat, lng: r.live_lng, at: r.live_location_at }}
                trackingEnabled={r.tracking_enabled}
                roadLegs={roadLegs}
              />
            </div>
          </div>

          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-text-main">Stops (in delivery order)</h2>
            {!selectMode ? (
              <Button
                variant="outline" size="sm"
                disabled={pendingIds.length === 0}
                title={pendingIds.length === 0 ? "No pending stops to mark" : undefined}
                onClick={() => setSelectMode(true)}
                data-testid="route-mark-delivered-button"
              >
                <CheckCircle2 size={15} strokeWidth={2} /> Mark Delivered
              </Button>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-text-muted">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="route-select-all" /> Select all pending
                </label>
                <span className="text-xs text-text-muted" data-testid="route-selected-count">{selectedIds.size} selected · drag over rows to select</span>
                <Button
                  variant="accent" size="sm"
                  disabled={selectedIds.size === 0 || marking}
                  onClick={handleMarkDelivered}
                  data-testid="route-confirm-delivered"
                >
                  <CheckCircle2 size={15} strokeWidth={2} /> {marking ? "Marking..." : `Mark ${selectedIds.size} Delivered`}
                </Button>
                <Button variant="secondary" size="sm" onClick={exitSelect} data-testid="route-select-cancel">Cancel</Button>
              </div>
            )}
          </div>
          <div ref={stopsRef}>
          <div
            ref={tableWrapRef}
            className="relative"
            onMouseDown={onWrapMouseDown}
            onMouseMove={onWrapMouseMove}
            onMouseUp={endMarquee}
            onMouseLeave={endMarquee}
            style={selectMode ? { userSelect: "none" } : undefined}
          >
          <DataTable
            testId="route-stops-table"
            rows={r.deliveries}
            emptyTitle="No stops on this route"
            rowClassName={(d) => {
              const hl = highlightId && String(d.id) === String(highlightId) ? "animate-highlight-blink" : "";
              const sel = selectMode && selectedIds.has(d.id) ? "bg-primary/10" : "";
              return `${hl} ${sel}`.trim();
            }}
            columns={[
              ...(selectMode ? [{
                key: "select", header: (<Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="route-select-all-header" />), leading: true,
                render: (d) => {
                  const done = d.delivery_status === 2;
                  return <Checkbox checked={done || selectedIds.has(d.id)} disabled={done} onCheckedChange={() => toggleOne(d.id)} data-testid={`stop-select-${d.id}`} />;
                },
              }] : []),
              { key: "sequence", header: "#", leading: true, render: (d) => d.delivery_order_id ?? "—" },
              {
                key: "customer", header: "Customer", primary: true,
                render: (d) => <Link to={`/customers/${d.customer_id}`} className="text-primary hover:underline" data-testid={`stop-customer-link-${d.id}`}>{d.customer_name || `#${d.customer_id}`}</Link>,
              },
              {
                key: "subscription", header: "Subscription",
                render: (d) => <Link to={`/subscriptions/${d.subscription_id}`} className="text-primary hover:underline" data-testid={`stop-subscription-link-${d.id}`}>#{d.subscription_id}</Link>,
              },
              { key: "address", header: "Address", render: (d) => d.address || "—" },
              {
                key: "status", header: "Status",
                render: (d) => {
                  if (d.delivery_status === 2 && d.delivered_by_admin) {
                    return <StatusBadge label="Delivered by Admin" variant="success" data-testid={`stop-admin-delivered-${d.id}`} />;
                  }
                  const m = deliveryStatusMeta(d.delivery_status); return <StatusBadge label={m.label} variant={m.variant} />;
                },
              },
              {
                key: "packing", header: "Packing",
                render: (d) => { const m = packingStatusMeta(d.packing_status); return <StatusBadge label={m.label} variant={m.variant} data-testid={`stop-packing-status-${d.id}`} />; },
              },
              {
                key: "eta", header: "ETA",
                render: (d) => (
                  <span className="text-xs text-text-muted" data-testid={`stop-eta-${d.id}`}>
                    {d.delivery_status === 1 ? (d.eta_label || "Not started") : "—"}
                  </span>
                ),
              },
              {
                key: "pod", header: "Actions", footer: true,
                render: (d) => (
                  <div className="flex flex-wrap items-center gap-2">
                    {(d.pof1 || d.pof2) ? (
                      <button
                        type="button" onClick={() => setPodDelivery(d)}
                        className="flex items-center gap-1 text-xs text-primary hover:underline"
                        data-testid={`stop-pod-button-${d.id}`}
                      >
                        <ImageIcon size={13} strokeWidth={1.5} /> View POD
                      </button>
                    ) : <span className="text-xs text-text-muted">No POD</span>}
                    <Button
                      variant="outline" size="sm" disabled={locked} title={locked ? "Locked — packing has started" : undefined}
                      onClick={() => setMoving(d)} data-testid={`stop-move-button-${d.id}`}
                    >
                      Move
                    </Button>
                    <Button
                      variant="outline" size="icon"
                      className={d.special_instruction ? "border-warning/50 text-warning" : ""}
                      title={d.special_instruction ? "Edit special instruction" : "Add special instruction"}
                      onClick={() => setInstructionDelivery(d)}
                      data-testid={`stop-special-instruction-button-${d.id}`}
                    >
                      <Megaphone size={15} strokeWidth={1.5} />
                    </Button>
                  </div>
                ),
              },
            ]}
          />
          {marquee && (
            <div
              className="pointer-events-none absolute z-10 rounded-sm border-2 border-primary/70 bg-primary/10"
              style={{
                left: Math.min(marquee.x0, marquee.x1),
                top: Math.min(marquee.y0, marquee.y1),
                width: Math.abs(marquee.x1 - marquee.x0),
                height: Math.abs(marquee.y1 - marquee.y0),
              }}
            />
          )}
          </div>
          </div>
        </TabsContent>

        <TabsContent value="reoptimize" className="mt-4" data-testid="route-detail-reoptimize-tab">
          {locked ? (
            <p className="mb-4 flex items-center gap-1.5 rounded-md border border-warning/40 bg-warning-bg p-3 text-xs font-medium text-warning" data-testid="route-detail-reoptimize-locked-note">
              <Lock size={14} strokeWidth={2} /> Locked — packing has started for this route. It can no longer be re-optimized, recalculated, or have stops moved.
            </p>
          ) : (
            <p className="mb-3 text-xs text-text-muted">
              Review the map and stops below, then try a different sequence. Nothing is applied to the driver until you click a button here.
            </p>
          )}

          <div className="mb-4 flex flex-wrap items-center gap-2">
            <label className="flex w-fit items-center gap-2 rounded-md border border-border bg-surface px-3 py-1.5 text-xs" data-testid="route-detail-use-real-distance-toggle">
              <input
                type="checkbox" checked={useRealDistance} disabled={locked}
                onChange={(e) => setUseRealDistance(e.target.checked)}
                className="h-3.5 w-3.5 accent-primary"
              />
              Use real road distance (ORS) when re-optimizing
              <span className="text-text-muted">· currently at variant {variant} of 6</span>
            </label>
            {canRecalculate && (
              <Button
                variant="outline" disabled={recalculating || locked} title={locked ? "Locked — packing has started" : undefined}
                onClick={handleRecalculateRealDistance} data-testid="route-detail-recalculate-real-distance-button"
              >
                <RefreshCcw size={16} strokeWidth={1.5} /> {recalculating ? "Recalculating..." : "Recalculate with Real Road Distance"}
              </Button>
            )}
            <Button
              variant="accent" disabled={reoptimizing || locked || !r.deliveries?.length} title={locked ? "Locked — packing has started" : undefined}
              onClick={handleReoptimize} data-testid="route-detail-reoptimize-button"
            >
              <RefreshCcw size={16} strokeWidth={1.5} /> {reoptimizing ? "Re-optimizing..." : "Re-optimize"}
            </Button>
          </div>

          <div className="mb-6 h-[380px] overflow-hidden rounded-md border border-border" style={{ isolation: "isolate" }} data-testid="route-detail-reoptimize-map-container">
            <RouteMap
              outlet={outlet} driver={driver} stops={r.deliveries}
              live={{ lat: r.live_lat, lng: r.live_lng, at: r.live_location_at }}
              trackingEnabled={r.tracking_enabled}
              roadLegs={roadLegs}
            />
          </div>

          <h2 className="mb-2 text-sm font-semibold text-text-main">Stops (in delivery order)</h2>
          <ol className="divide-y divide-border rounded-md border border-border bg-surface" data-testid="route-detail-reoptimize-stop-list">
            {r.deliveries?.map((d) => (
              <li key={d.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`route-detail-reoptimize-stop-${d.id}`}>
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">{d.delivery_order_id ?? "—"}</span>
                <span className="font-medium text-text-main">{d.customer_name || `#${d.customer_id}`}</span>
                <span className="truncate text-xs text-text-muted">{d.address || "—"}</span>
              </li>
            ))}
          </ol>
        </TabsContent>
      </Tabs>

      <ChangeDriverDialog
        open={changeDriverOpen} onOpenChange={setChangeDriverOpen}
        routeId={r.id} currentDriverId={r.driver_id} currentVersion={r.updated_at}
        onDone={() => { setChangeDriverOpen(false); setConflict(null); load(); }}
        onConflict={() => load(true)}
      />
      <MoveDeliveryDialog
        open={!!moving} onOpenChange={(v) => !v && setMoving(null)}
        delivery={moving} currentRouteId={r.id} date={r.date}
        onDone={() => { setMoving(null); load(); }}
      />
      <PodViewerDialog
        open={!!podDelivery} onOpenChange={(v) => !v && setPodDelivery(null)}
        delivery={podDelivery}
      />
      <SpecialInstructionDialog
        delivery={instructionDelivery}
        onOpenChange={(v) => !v && setInstructionDelivery(null)}
        onSaved={() => load(true)}
      />
      <ConfirmDialog
        open={deleteOpen} onOpenChange={setDeleteOpen}
        title={`Delete Route #${r.id}?`}
        description="All of this route's stops will return to the unassigned pool for this date and can be re-optimized/reassigned in Route Builder. This cannot be undone."
        confirmLabel="Delete Route" variant="danger" loading={deleting} onConfirm={handleDeleteRoute}
        testId="route-detail-delete-confirm-dialog"
      />
    </div>
  );
}
